import { TIE_RESULT, keyGameLabel } from "@averagejoesff/shared";
import type { KeyGame, Rivalry } from "@averagejoesff/shared";
import { formatPoints } from "../../lib/format";
import { FactList } from "./FactList";
import type { ManagerLookup } from "./managers";
import { managerName } from "./managers";
import { seriesSummary } from "./series";

/**
 * Points above or below projection before a key game calls it out. Higher
 * than the weekly facts' bar: weekly scores sit around 140, so a 20-point
 * swing is routine across a whole rivalry's key games.
 */
const NOTABLE_PROJECTION_SWING = 30;

/*
 * Both pieces take a rivalry already oriented so index 0 is the left-hand
 * manager (see `findRivalry`). Everything is coloured by side — left in
 * `--side-left`, right in `--side-right` — so the bar, the pips and the
 * scores all read against the names without a separate legend.
 */

interface RivalryProps {
  rivalry: Rivalry;
  managers: ManagerLookup;
}

/** The at-a-glance part: series record and recent form. */
export function RivalrySummary({ rivalry, managers }: RivalryProps) {
  return (
    <div className="rivalry">
      <RecordBar rivalry={rivalry} managers={managers} />
      <FormStrip rivalry={rivalry} managers={managers} />
    </div>
  );
}

/** The deep-dive part: notable games and lasting rivalry facts. */
export function RivalryHistory({ rivalry, managers }: RivalryProps) {
  return (
    <div className="rivalry">
      <KeyGameList rivalry={rivalry} managers={managers} />
      <FactList facts={rivalry.facts} heading="History" />
    </div>
  );
}

function RecordBar({ rivalry, managers }: RivalryProps) {
  const [leftId, rightId] = rivalry.managerIds;
  const [leftWins, rightWins] = rivalry.wins;
  const decided = leftWins + rightWins;
  // An all-ties (or empty) history splits the bar down the middle.
  const leftShare = decided === 0 ? 50 : (leftWins / decided) * 100;
  const leftName = managerName(managers, leftId);
  const rightName = managerName(managers, rightId);

  return (
    <div className="record-bar">
      <div className="record-bar__summary">
        <span>{seriesSummary(rivalry, managers)}</span>
        <span className="record-bar__games">
          {rivalry.gamesPlayed} {rivalry.gamesPlayed === 1 ? "meeting" : "meetings"}
        </span>
      </div>
      <div
        className="record-bar__track"
        role="img"
        aria-label={`${leftName} ${leftWins} wins, ${rightName} ${rightWins} wins`}
      >
        <div className="record-bar__fill" style={{ width: `${leftShare}%` }} />
      </div>
      <div className="record-bar__stats">
        <span className="record-bar__side record-bar__side--left">
          <span className="record-bar__name">{leftName}</span>
          <span>
            <strong>{leftWins}</strong> W · {formatPoints(rivalry.pointsPerGame[0])} ppg
          </span>
        </span>
        {rivalry.playoffWins && (
          <span className="record-bar__playoffs" aria-label={`Playoffs: ${leftName} ${rivalry.playoffWins[0]}, ${rightName} ${rivalry.playoffWins[1]}`}>
            Playoffs <span className="record-bar__playoff-wins--left">{rivalry.playoffWins[0]}</span>–
            <span className="record-bar__playoff-wins--right">{rivalry.playoffWins[1]}</span>
          </span>
        )}
        <span className="record-bar__side record-bar__side--right">
          <span className="record-bar__name">{rightName}</span>
          <span>
            <strong>{rightWins}</strong> W · {formatPoints(rivalry.pointsPerGame[1])} ppg
          </span>
        </span>
      </div>
    </div>
  );
}

/** Recent meetings as pips, each filled in the winner's side colour. */
function FormStrip({ rivalry, managers }: RivalryProps) {
  const results = rivalry.recentResults;
  if (results.length === 0) return null;
  const [leftId] = rivalry.managerIds;

  const side = (winnerId: string) => (winnerId === TIE_RESULT ? "tie" : winnerId === leftId ? "left" : "right");

  return (
    <div className="form-strip">
      <span className="form-strip__label">Last {results.length}</span>
      <ol className="form-strip__pips" aria-label={`Winners of the last ${results.length} meetings, oldest first`}>
        {results.map((winnerId, i) => (
          <li key={i} className={`form-strip__pip form-strip__pip--${side(winnerId)}`}>
            <span className="visually-hidden">{winnerId === TIE_RESULT ? "Tie" : managerName(managers, winnerId)}</span>
          </li>
        ))}
      </ol>
      {/* Points back at the newest pip; short enough to stay on the pips' line on a phone. */}
      <span className="form-strip__hint" aria-hidden="true">
        ← latest
      </span>
    </div>
  );
}

function KeyGameList({ rivalry, managers }: RivalryProps) {
  const games = rivalry.keyGames;
  if (games.length === 0) return null;
  return (
    <ul className="key-games">
      {games.map((game, i) => {
        const label = keyGameLabel(game);
        const projection = projectionCallout(game, rivalry.managerIds, managers);
        return (
          <li key={i} className="key-game">
            <div className="key-game__head">
              <span className="key-game__label">{label}</span>
              <span className="key-game__when">
                {game.season} · Wk {game.week}
                {/* "Playoff clash" already says so; a custom title like "Ugliest win" doesn't. */}
                {game.isPlayoff && !/playoff/i.test(label) && <span className="tag key-game__playoff">Playoffs</span>}
              </span>
            </div>
            <Scoreline scores={game.scores} />
            {projection && <div className="key-game__projected">{projection}</div>}
            {game.note && <p className="key-game__note">{game.note}</p>}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * "Blake beat projection by 67.00" for whichever side missed or beat
 * Yahoo's projection the most, or null when neither swing is notable —
 * a projection close to the result isn't worth the space.
 */
function projectionCallout(game: KeyGame, managerIds: readonly [string, string], managers: ManagerLookup): string | null {
  if (!game.projected) return null;
  const swings = managerIds.map((id, side) => ({ id, swing: game.scores[side]! - game.projected![side]! }));
  const biggest = swings.reduce((a, b) => (Math.abs(b.swing) > Math.abs(a.swing) ? b : a));
  if (Math.abs(biggest.swing) < NOTABLE_PROJECTION_SWING) return null;
  const verb = biggest.swing > 0 ? "beat" : "missed";
  return `${managerName(managers, biggest.id)} ${verb} projection by ${formatPoints(Math.abs(biggest.swing))}`;
}

function Scoreline({ scores }: { scores: [number, number] }) {
  const [left, right] = scores;
  const cls = (side: "left" | "right", mine: number, theirs: number) =>
    `key-game__score${mine > theirs ? ` key-game__score--winner key-game__score--${side}` : ""}`;
  return (
    <div className="key-game__scores">
      <span className={cls("left", left, right)}>{formatPoints(left)}</span>
      <span className="key-game__dash">–</span>
      <span className={cls("right", right, left)}>{formatPoints(right)}</span>
    </div>
  );
}
